import { useEffect } from 'react'
import { useFormikContext } from 'formik'
import type {
  TeamPlayerFinderFormValues,
  TeamPlayerFinderRegistration,
} from '@sportninja/common/features/team/playerFinder/types'
import { toPlayerFinderFormValues } from '@sportninja/common/features/team/playerFinder/utils/settingsForm'
import type { RecruitingSettingsSave } from './useRecruitingSettingsSave'

/**
 * Show fresh settings that arrive while the rep has NOT changed anything — a
 * background refetch, a save from the Team Budget sheet — and base the edit on
 * them.
 *
 * Deliberately inert while the form has changes: the edit stays based on what the
 * rep opened, so Save sends that revision and the server answers 409 instead of
 * letting a stale choice overwrite a change the rep never saw. The 409 itself is
 * handled by useRecruitingSettingsSave.
 *
 * Must be called inside the card's <Formik>.
 */
export const useAdoptFreshSettings = (
  entry: TeamPlayerFinderRegistration,
  save: Pick<RecruitingSettingsSave, 'baseRevision' | 'adoptBase'>
): void => {
  const { dirty, resetForm } = useFormikContext<TeamPlayerFinderFormValues>()
  const { baseRevision, adoptBase } = save

  useEffect(() => {
    if (entry.revision === baseRevision || dirty) {
      return
    }
    adoptBase(entry)
    resetForm({ values: toPlayerFinderFormValues(entry) })
  }, [entry, baseRevision, adoptBase, dirty, resetForm])
}

export default useAdoptFreshSettings
